import { ChildSessionRenewResponseSchema } from "@oyako-flow/contracts";
import { createOyakoFlowClient } from "../../../clients/typescript/src/index";

type RenewedSession = ReturnType<typeof ChildSessionRenewResponseSchema.parse>;
let active: {
  workspaceId: string;
  childId: string;
  deviceId: string;
  renewAt: number;
  onRenew: (session: RenewedSession) => void;
} | null = null;
let pending: Promise<void> | null = null;

export function configureChildSession(value: Omit<NonNullable<typeof active>, "renewAt"> | null) {
  active = value === null ? null : { ...value, renewAt: 0 };
}

function csrfCookie(): string {
  const value = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("oyako_csrf="));
  return value === undefined ? "" : decodeURIComponent(value.slice("oyako_csrf=".length));
}

export async function ensureChildSession(workspaceId?: string): Promise<void> {
  const binding = active;
  if (
    binding === null ||
    (workspaceId !== undefined && workspaceId !== binding.workspaceId) ||
    Date.now() < binding.renewAt
  )
    return;
  if (pending !== null) return pending;
  const renew = async () => {
    if (active !== binding) return;
    const client = createOyakoFlowClient(location.origin);
    const { data, error, response } = await client.POST("/api/v1/auth/child-session/renew", {
      params: {
        header: {
          "X-Workspace-Id": binding.workspaceId,
          "X-CSRF-Token": csrfCookie(),
          "Idempotency-Key": crypto.randomUUID(),
        },
      },
      credentials: "same-origin",
      signal: AbortSignal.timeout(15_000),
    });
    if (error !== undefined || !response.ok) {
      throw new Error(
        response.status === 401 || response.status === 403
          ? "おうちのひとに たんまつの とうろくを おねがいしてね。"
          : "つうしんを かくにんして、もういちど ためしてね。",
      );
    }
    const session = ChildSessionRenewResponseSchema.parse(data);
    if (
      session.actor.type !== "child" ||
      session.workspace.id !== binding.workspaceId ||
      session.actor.bound_child_id !== binding.childId ||
      session.actor.device_id !== binding.deviceId
    ) {
      throw new Error("たんまつの とうろくを かくにんしてね。");
    }
    if (active === binding) {
      binding.renewAt = Date.parse(session.renew_after);
      binding.onRenew(session);
    }
  };
  pending = (
    "locks" in navigator
      ? navigator.locks.request(
          `oyako-child-renew:${binding.workspaceId}:${binding.deviceId}`,
          renew,
        )
      : renew()
  )
    .then(() => undefined)
    .finally(() => {
      pending = null;
    });
  return pending;
}
